<template>
	<div class="runConfirmRoot" role="dialog" aria-labelledby="runConfirmTitle" aria-describedby="runConfirmBody">
		<div class="titleBar" :class="{ titleBarPreview: readOnly }">
			<div id="runConfirmTitle" class="title" role="alert">{{title}}</div>
			<div class="subtitle">{{subtitle}}</div>
		</div>
		<div id="runConfirmBody" class="dialogBody" ref="dialogBody">
			<div v-if="simulated" class="dryRunTag">
				SIMULATED (dry-run mode) — this instance is not the authorized orchestrator.  Every step below is rehearsed; nothing touches a live server.
			</div>
			<div v-if="kind === 'CutoverOnly'" class="staleWarning" role="alert">
				<div class="staleTitle">WARNING — the target's data may be stale</div>
				No database migration happens.  The web servers are repointed at
				<code class="inline">{{targetName}}</code> exactly as its databases are right now.  Unless those databases were <em>just</em>
				migrated (or you are deliberately recovering to an older copy), writes made since the last migration will be missing.
				The target databases must be ONLINE.
			</div>
			<div v-else-if="kind === 'ResumeMigration'" class="noteBox">
				Resumes the interrupted DbMigrate job from its persisted state: interrupted steps are re-run, base backup files are re-verified
				and RESTORING targets are restored over.  <strong>Approval at the cutover gate is required again</strong> — the run will wait
				for you to click <em>Approve cutover</em> in the operation panel before the final cutover.
			</div>
			<div v-else-if="kind === 'TakeLocalBaseBackups'" class="noteBox">
				Takes a <strong>fresh full backup</strong> of each migrated database on the target into the target's own backup folder, so the
				target can restore from its own local files.  A full backup of a large database takes time and I/O; you may prefer to time
				it outside business hours.  Failures come back as warnings; the target's next scheduled full backup heals the same exposure.
			</div>
			<div v-else-if="kind === 'RollbackLastCutover'" class="noteBox">
				Restores the <strong>exact previous AppSettings contents</strong> recorded in the last cutover plan on every web server and
				recycles their app pools, repointing the fleet at the previous database server.  No database is migrated; writes that landed
				on the current server since the cutover are not carried back.
			</div>
			<div v-else-if="kind === 'MigrateAndCutover'" class="noteBox">
				Migrates the selected databases from the source to the target with DbMigrate (base backup and restore, delta catch-up rounds,
				then a short final cutover), then repoints every web server at the target and recycles their app pools.  The web apps are
				briefly unavailable while the pools recycle.
			</div>
			<div v-else-if="kind === 'MigrateOnly'" class="noteBox">
				Migrates the selected databases from the source to the target with DbMigrate (base backup and restore, delta catch-up rounds,
				then a short final cutover) and <strong>leaves every web server where it is</strong>: no AppSettings edit, no app-pool recycle,
				and the source databases stay ONLINE and READ_WRITE.  Use it for databases the web apps do not connect to, or to stage a copy
				before a separate <em>Cut over only</em>.
			</div>

			<div class="kvGrid" v-if="request">
				<template v-if="kind !== 'RollbackLastCutover'">
					<span class="kvKey">Direction</span>
					<span><code class="inline">{{sourceName}}</code> → <code class="inline">{{targetName}}</code></span>
					<span class="kvKey">Databases</span>
					<span>
						{{dbNamesText}}
						<template v-if="renameMode"> <span class="mutedText">(rename-mode rehearsal → {{targetDbNamesText}}; the web-app cutover is skipped)</span></template>
					</span>
				</template>
				<template v-if="kind === 'MigrateAndCutover' || kind === 'MigrateOnly' || kind === 'ResumeMigration'">
					<span class="kvKey">Cutover target</span>
					<span>{{request.cutoverTargetSeconds}} s <span class="mutedText">(warn-only; exceeding it never fails the run)</span></span>
					<span class="kvKey">Cutover gate</span>
					<span v-if="request.holdAtGate || kind === 'ResumeMigration'"><strong>Hold</strong> — the migration waits at DbMigrate's operator gate until you click <em>Approve cutover</em>.</span>
					<span v-else>Automatic — the orchestrator approves once its pre-cutover checks pass.</span>
				</template>
				<template v-if="preview && kind !== 'RollbackLastCutover' && kind !== 'TakeLocalBaseBackups'">
					<span class="kvKey">Web-app cutover</span>
					<span v-if="preview.webAppCutover"><strong>Yes</strong> — every web server is repointed at <code class="inline">{{targetName}}</code>.</span>
					<span v-else><strong>No</strong> — the web servers stay where they are<template v-if="kind === 'ResumeMigration'"> (the job was started without a web-app cutover)</template>.</span>
				</template>
			</div>

			<template v-if="preview">
				<div class="sectionTitle">What will happen, in order</div>
				<ol v-if="steps.length" class="stepList">
					<li v-for="(s, i) in steps" :key="i">{{s}}</li>
				</ol>
				<div v-else class="mutedText">The server did not list any steps.</div>

				<template v-if="servers.length">
					<div class="sectionTitle">Web servers ({{servers.length}})</div>
					<div class="tableScroll">
						<table class="serverTable">
							<thead>
								<tr>
									<th>Server</th>
									<th>Release</th>
									<th>TaylorDbServer</th>
									<th>UmlsDbServer</th>
									<th>Change</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="s in servers" :key="s.agentName" :class="{ noChangeRow: rowNoChange(s) }">
									<td>
										<div class="serverName">{{s.agentName}}</div>
										<div v-if="s.appSettingsPath" class="pathText" :title="s.appSettingsPath">{{s.appSettingsPath}}</div>
									</td>
									<td>{{s.releaseName || '—'}}</td>
									<td>
										<template v-if="valueUnchanged(s.currentTaylorServer, s.newTaylorServer)"><code class="inline">{{s.newTaylorServer}}</code> <span class="mutedText">(unchanged)</span></template>
										<template v-else><code class="inline">{{s.currentTaylorServer || '?'}}</code> → <code class="inline newValue">{{s.newTaylorServer || '?'}}</code></template>
									</td>
									<td>
										<template v-if="valueUnchanged(s.currentUmlsServer, s.newUmlsServer)"><code class="inline">{{s.newUmlsServer}}</code> <span class="mutedText">(unchanged)</span></template>
										<template v-else><code class="inline">{{s.currentUmlsServer || '?'}}</code> → <code class="inline newValue">{{s.newUmlsServer || '?'}}</code></template>
									</td>
									<td>
										<span v-if="rowNoChange(s)" class="stateBadge badgeOff" title="This server is already on the target and its AppSettings.spdx already holds the target values; nothing will be written or recycled.">no change needed</span>
										<span v-else class="diffText">{{s.diffSummary || 'two server values change'}}</span>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</template>

				<div v-if="preview.expectedImpact" class="impactBox">
					<strong>Expected impact:</strong> {{preview.expectedImpact}}
				</div>

				<div v-if="warnings.length" class="warningBox">
					<strong>{{warnings.length}} warning{{warnings.length === 1 ? '' : 's'}} — not blocking, but read before continuing:</strong>
					<ul>
						<li v-for="(w, i) in warnings" :key="i">{{w}}</li>
					</ul>
				</div>

				<div v-if="blockers.length" class="blockerBox" role="alert">
					<strong>This operation cannot start ({{blockers.length}} blocker{{blockers.length === 1 ? '' : 's'}}):</strong>
					<ul>
						<li v-for="(b, i) in blockers" :key="i">{{b}}</li>
					</ul>
				</div>
			</template>
			<div v-else class="noPreview">
				{{noPreviewText}}
			</div>
		</div>
		<div class="footer">
			<template v-if="!readOnly">
				<div v-if="!canStart" class="footerNote">
					<template v-if="blockers.length">Resolve the blockers above, then open this dialog again.</template>
					<template v-else-if="!preview">Without a preview from the server the operation cannot be confirmed.</template>
				</div>
				<div v-else class="slideWrap" :class="{ slideRed: destructive }">
					<slide-button :key="'slide_runConfirm_' + kind"
								  :auto-width="false"
								  :circle="true"
								  :noanimate="true"
								  :width="320"
								  :height="50"
								  :text="slideText"
								  success-text="starting"
								  :requireReleaseToComplete="true"
								  @completed="onConfirm" />
				</div>
			</template>
			<div v-else class="footerNote previewNote">Preview only — nothing was executed and nothing will be.</div>
			<div ref="closeBtn" class="dialogButton cancelButton" role="button" tabindex="0" @click="DefaultClose" @keydown.space.enter.prevent="DefaultClose">
				{{readOnly ? 'Close' : 'Cancel'}}
			</div>
		</div>
	</div>
</template>

<script>
	// Confirmation / preview dialog for a maintenance run.  Opened through ModalDialog(RunConfirmPopup, props, options);
	// resolves true when the administrator completes the slide control, false otherwise.  In readOnly mode (the
	// "Dry-run / preview" action) there is no start control at all.
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { runKindLabel, joinNames } from './runFormat';

	export default {
		components: { SlideButton },
		props:
		{
			/** DbMaintenanceRunKind being confirmed. */
			kind: {
				type: String,
				required: true
			},
			/** DbMaintenancePreview from the Preview action, or null when the server returned none. */
			preview: {
				type: Object,
				default: null
			},
			/** The DbMaintenanceRunRequest that will be sent (source/target/dbNames/cutoverTargetSeconds/holdAtGate). */
			request: {
				type: Object,
				default: null
			},
			/** True for the "Dry-run / preview" action: no start control. */
			readOnly: {
				type: Boolean,
				default: false
			},
			/** True when the feature runs in dry-run (simulated) mode.  A preview carries its own dryRun flag (the mode it was built in), which wins. */
			dryRun: {
				type: Boolean,
				default: false
			},
			/** Explanation shown when no preview is available. */
			noPreviewText: {
				type: String,
				default: 'The server did not return a preview.'
			}
		},
		computed:
		{
			title()
			{
				return (this.readOnly ? 'Preview: ' : 'Confirm: ') + runKindLabel(this.kind);
			},
			subtitle()
			{
				if (this.readOnly)
					return 'Exactly what a run of this kind would do right now, without executing anything.';
				return 'Read every step below.  Sliding the control at the bottom starts the operation immediately.';
			},
			/**
			 * Whether what this dialog describes is simulated.  The preview says which mode it was actually built in (the mode
			 * MaintenanceOutbound selected at startup); the page-level flag is the fallback when there is no preview.
			 */
			simulated()
			{
				if (this.preview && typeof this.preview.dryRun === 'boolean')
					return this.preview.dryRun || this.dryRun;
				return this.dryRun;
			},
			sourceName()
			{
				return (this.preview && this.preview.sourceServer) || (this.request && this.request.sourceServerName) || '?';
			},
			targetName()
			{
				return (this.preview && this.preview.targetServer) || (this.request && this.request.targetServerName) || '?';
			},
			dbNamesText()
			{
				let names = (this.preview && this.preview.dbNames) || (this.request && this.request.dbNames) || [];
				return names.length ? joinNames(names) : '(none)';
			},
			renameMode()
			{
				let t = (this.preview && this.preview.targetDbNames) || (this.request && this.request.targetDbNames);
				return Array.isArray(t) && t.length > 0;
			},
			targetDbNamesText()
			{
				return joinNames((this.preview && this.preview.targetDbNames) || (this.request && this.request.targetDbNames) || []);
			},
			steps()
			{
				return this.preview && Array.isArray(this.preview.steps) ? this.preview.steps : [];
			},
			servers()
			{
				return this.preview && Array.isArray(this.preview.servers) ? this.preview.servers : [];
			},
			warnings()
			{
				return this.preview && Array.isArray(this.preview.warnings) ? this.preview.warnings : [];
			},
			blockers()
			{
				return this.preview && Array.isArray(this.preview.blockers) ? this.preview.blockers : [];
			},
			canStart()
			{
				return !this.readOnly && !!this.preview && this.blockers.length === 0;
			},
			/** Recovery tools get a red slide control as a visual cue. */
			destructive()
			{
				return this.kind === 'CutoverOnly' || this.kind === 'RollbackLastCutover';
			},
			slideText()
			{
				switch (this.kind)
				{
					case 'MigrateAndCutover': return 'slide to migrate + cut over';
					case 'MigrateOnly': return 'slide to migrate (no web-app cutover)';
					case 'CutoverOnly': return 'slide to cut over (stale data risk)';
					case 'RollbackLastCutover': return 'slide to roll back';
					case 'ResumeMigration': return 'slide to resume migration';
					case 'TakeLocalBaseBackups': return 'slide to take base backups';
					default: return 'slide to start';
				}
			}
		},
		methods:
		{
			/**
			 * True when a server element keeps its exact value — the same ordinal rule the agent applies (AppSettingsDbEdit): a
			 * value spelled differently (FQDN vs. bare name, case) is rewritten, so it is shown as a change.
			 */
			valueUnchanged(current, next)
			{
				return typeof next === 'string' && next.length > 0 && current === next;
			},
			/**
			 * The "no change needed" badge is shown only when the server says so AND the values beside it agree; a row can never read
			 * "db5 → devdb … no change needed".
			 */
			rowNoChange(s)
			{
				return !!s.noChangeNeeded && this.valueUnchanged(s.currentTaylorServer, s.newTaylorServer) && this.valueUnchanged(s.currentUmlsServer, s.newUmlsServer);
			},
			SetFocus()
			{
				if (this.$refs.closeBtn)
					this.$refs.closeBtn.focus();
			},
			DefaultClose()
			{
				this.$emit('close', false);
			},
			onConfirm()
			{
				if (this.canStart)
					this.$emit('close', true);
			}
		}
	}
</script>

<style scoped>
	.runConfirmRoot
	{
		background-color: #FFFFFF;
		min-width: 320px;
		max-width: min(960px, calc(100vw - 30px));
		max-height: calc(100vh - 30px);
		display: flex;
		flex-direction: column;
		word-break: break-word;
	}

	.titleBar
	{
		padding: 12px 16px 8px 16px;
		border-bottom: 1px solid #E0E0E0;
		flex: 0 0 auto;
	}

		.titleBar.titleBarPreview
		{
			background-color: #F4F4F4;
		}

	.title
	{
		text-align: center;
		font-weight: bold;
		font-size: 16pt;
	}

	.subtitle
	{
		text-align: center;
		color: #555555;
		font-size: 0.9em;
		margin-top: 2px;
	}

	.dialogBody
	{
		padding: 10px 16px 12px 16px;
		overflow-y: auto;
		flex: 1 1 auto;
		min-height: 0;
	}

	.footer
	{
		flex: 0 0 auto;
		border-top: 1px solid #E0E0E0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: 10px 16px;
		padding: 10px 16px;
	}

	.footerNote
	{
		color: #7A5200;
		font-style: italic;
		font-size: 0.95em;
	}

		.footerNote.previewNote
		{
			color: #555555;
		}

	.dialogButton
	{
		display: inline-block;
		cursor: pointer;
		font-weight: bold;
		font-size: 12pt;
		padding: 10px 22px;
		border-radius: 5px;
		color: #CE7D29;
		border: 1px solid #DDDDDD;
		margin-left: auto;
	}

		.dialogButton:hover,
		.dialogButton:focus
		{
			background-color: rgba(0,0,0,0.05);
		}

	.slideWrap :deep(.slideunlock)
	{
		margin: 0;
	}

	.slideRed :deep(.slideunlock)
	{
		--su-color-bg: #F5DCDC;
		--su-color-progress-normal-bg: #E5AFAF;
		--su-color-progress-complete-bg: #CC3333;
	}

	.dryRunTag
	{
		background: repeating-linear-gradient(135deg, #FFB347 0px, #FFB347 14px, #FFC97A 14px, #FFC97A 28px);
		border: 2px solid #A85A00;
		color: #3A2000;
		font-weight: bold;
		padding: 8px 12px;
		margin-bottom: 10px;
	}

	.staleWarning
	{
		background-color: #FFE0E0;
		border: 2px solid #C00000;
		color: #660000;
		padding: 10px 14px;
		margin-bottom: 10px;
		line-height: 1.5;
	}

	.staleTitle
	{
		font-weight: bold;
		font-size: 1.15em;
		margin-bottom: 4px;
	}

	.noteBox
	{
		background-color: #F4F7FB;
		border: 1px solid #C9D8EA;
		color: #1F3A5C;
		padding: 8px 12px;
		margin-bottom: 10px;
		line-height: 1.5;
	}

	.kvGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 4px 14px;
		align-items: baseline;
		margin: 6px 0 10px 0;
	}

	.kvKey
	{
		font-weight: bold;
		color: #444444;
		white-space: nowrap;
	}

	.mutedText
	{
		color: #888888;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

		code.inline.newValue
		{
			background-color: #D4F5D4;
			color: #0A5A0A;
			font-weight: bold;
		}

	.sectionTitle
	{
		font-weight: bold;
		font-size: 1.05em;
		border-bottom: 1px solid #E0E0E0;
		padding-bottom: 3px;
		margin: 12px 0 6px 0;
	}

	.stepList
	{
		margin: 4px 0 8px 0;
		padding-left: 28px;
		line-height: 1.6;
	}

		.stepList li
		{
			margin-bottom: 2px;
		}

	.tableScroll
	{
		overflow-x: auto;
	}

	.serverTable
	{
		border-collapse: collapse;
		width: 100%;
		font-size: 0.92em;
	}

		.serverTable th,
		.serverTable td
		{
			text-align: left;
			padding: 4px 8px;
			border-bottom: 1px solid #E4E4E4;
			vertical-align: top;
		}

		.serverTable th
		{
			color: #555555;
			font-size: 0.9em;
			white-space: nowrap;
		}

		.serverTable tr.noChangeRow td
		{
			color: #888888;
		}

	.serverName
	{
		font-weight: bold;
	}

	.pathText
	{
		font-family: Consolas, monospace;
		font-size: 0.8em;
		color: #888888;
		max-width: 260px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.diffText
	{
		font-family: Consolas, monospace;
		font-size: 0.9em;
	}

	.impactBox
	{
		background-color: #FAFAFA;
		border: 1px solid #E0E0E0;
		padding: 8px 12px;
		margin-top: 10px;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin-top: 10px;
	}

	.blockerBox
	{
		background-color: #FFE0E0;
		border: 2px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin-top: 10px;
	}

		.warningBox ul,
		.blockerBox ul
		{
			margin: 6px 0 0 0;
			padding-left: 22px;
		}

	.noPreview
	{
		background-color: #F4F4F4;
		border: 1px solid #DDDDDD;
		color: #555555;
		padding: 10px 12px;
		margin-top: 8px;
	}

	.stateBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
	}

	.badgeOff
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}
</style>
